import React, { useState } from 'react';
import { CartSidebar } from './CartSidebar';
import {
  Search,
  Heart,
  ArrowLeft,
  Share2,
  Check,
  Plus,
  Minus,
} from 'lucide-react';
export const RestaurantView = ({
  restaurant,
  onBack,
  onOpenItemModal,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onEditItem,
  onCheckout,
  onSaveForLater,
  onCreateGroupOrder,
  isSavingForLater,
  isCreatingGroupOrder,
  appliedPromo,
  onApplyPromo,
  discountAmount,
  taxRate,
}) => {
  const [menuSearch, setMenuSearch] = useState('');
  const [selectedMenuCategory, setSelectedMenuCategory] = useState('All');
  const [onlyVegetarian, setOnlyVegetarian] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedDescriptions(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).catch(() => {});
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    }
  };
  // Group or filter menu items
  const menuCategories = [
    'All',
    ...Array.from(new Set(restaurant.menu.map(m => m.category))),
  ];
  const isUnavailable = restaurant.availableForOrdering === false;
  const filteredItems = restaurant.menu.filter(item => {
    if (
      selectedMenuCategory !== 'All' &&
      item.category !== selectedMenuCategory
    )
      return false;
    if (onlyVegetarian && !item.isVegetarian) return false;
    if (menuSearch.trim()) {
      const q = menuSearch.toLowerCase();
      return (
        item.name.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
      );
    }
    return true;
  });
  return React.createElement(
    'div',
    { className: 'pb-20 space-y-6' },
    React.createElement(
      'div',
      { className: 'flex items-center justify-between' },
      React.createElement(
        'button',
        {
          onClick: onBack,
          className:
            'inline-flex items-center gap-2 text-xs sm:text-sm font-heading font-bold text-gray-700 hover:text-[#E53935] transition-colors group',
        },
        React.createElement(
          'div',
          {
            className:
              'w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center group-hover:border-[#E53935] group-hover:bg-red-50',
          },
          React.createElement(ArrowLeft, {
            className: 'w-4 h-4 text-gray-600 group-hover:text-[#E53935]',
          }),
        ),
        React.createElement('span', null, 'Back to All Restaurants'),
      ),
      React.createElement(
        'div',
        { className: 'flex items-center gap-2' },
        React.createElement(
          'button',
          {
            onClick: () => setIsFavorite(!isFavorite),
            className: `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              isFavorite
                ? 'bg-red-50 border-red-200 text-[#E53935]'
                : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
            }`,
          },
          React.createElement(Heart, {
            className: `w-3.5 h-3.5 ${
              isFavorite ? 'fill-[#E53935] text-[#E53935]' : ''
            }`,
          }),
          React.createElement(
            'span',
            null,
            isFavorite ? 'Saved to Favorites' : 'Save to Favorites',
          ),
        ),
        React.createElement(
          'button',
          {
            onClick: handleShare,
            className:
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-gray-200 text-gray-700 hover:border-gray-300 transition-all',
          },
          copiedShare
            ? React.createElement(Check, {
                className: 'w-3.5 h-3.5 text-[#65A30D]',
              })
            : React.createElement(Share2, { className: 'w-3.5 h-3.5' }),
          React.createElement(
            'span',
            null,
            copiedShare ? 'Link Copied!' : 'Share',
          ),
        ),
      ),
    ),
    React.createElement(
      'div',
      {
        className:
          'bg-white rounded-3xl border border-gray-200/90 shadow-sm overflow-hidden pt-3 mt-5',
      },
      React.createElement(
        'div',
        {
          className: 'relative h-56 sm:h-72 w-full overflow-hidden bg-gray-100',
        },
        React.createElement('img', {
          src: restaurant.coverImage,
          alt: restaurant.name,
          className: 'w-full h-full object-cover',
          referrerPolicy: 'no-referrer',
          onError: e => {
            e.target.onerror = null;
            e.target.src = '/panda_elegant_banner.jpg';
          },
        }),
        React.createElement('div', {
          className:
            'absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent',
        }),
        React.createElement(
          'div',
          {
            className:
              'absolute bottom-4 right-4 sm:bottom-6 sm:right-6 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white p-1 shadow-xl border-2 border-white overflow-hidden',
          },
          React.createElement('img', {
            src: restaurant.avatarImage,
            alt: restaurant.name,
            className: 'w-full h-full object-cover rounded-xl',
            referrerPolicy: 'no-referrer',
            onError: e => {
              e.target.onerror = null;
              e.target.src = '/panda_avatar.jpg';
            },
          }),
        ),
        React.createElement(
          'div',
          {
            className:
              'absolute bottom-4 left-4 sm:bottom-6 sm:left-6 text-white space-y-1 max-w-md',
          },
          React.createElement(
            'span',
            {
              className:
                'bg-[#E53935] text-white text-xs font-heading font-bold px-2.5 py-1 rounded-full uppercase tracking-wider',
            },
            restaurant.cuisine,
          ),
          React.createElement(
            'h1',
            {
              className:
                'text-2xl sm:text-4xl font-heading font-extrabold tracking-tight drop-shadow-sm',
            },
            restaurant.name,
          ),
          React.createElement(
            'p',
            {
              className:
                'text-white/90 text-xs sm:text-sm font-medium drop-shadow-xs',
            },
            'Ordered ',
            restaurant.orderedCount,
            ' times on Panda on Wheels',
          ),
        ),
      ),
      React.createElement(
        'div',
        {
          className:
            'grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 bg-panda-tint/20',
        },
        React.createElement(
          'div',
          { className: 'p-4 sm:p-5 text-center' },
          React.createElement(
            'p',
            {
              className:
                'text-[11px] sm:text-xs text-gray-500 font-medium uppercase tracking-wider',
            },
            'Delivery Fee',
          ),
          React.createElement(
            'p',
            {
              className:
                'font-heading font-bold text-sm sm:text-lg text-[#202124] mt-0.5',
            },
            '$',
            restaurant.deliveryFee.toFixed(2),
          ),
        ),
        React.createElement(
          'div',
          { className: 'p-4 sm:p-5 text-center' },
          React.createElement(
            'p',
            {
              className:
                'text-[11px] sm:text-xs text-gray-500 font-medium uppercase tracking-wider',
            },
            'Order Notice',
          ),
          React.createElement(
            'p',
            {
              className:
                'font-heading font-bold text-sm sm:text-lg text-[#202124] mt-0.5',
            },
            restaurant.deliveryTime,
          ),
        ),
        React.createElement(
          'div',
          { className: 'p-4 sm:p-5 text-center' },
          React.createElement(
            'p',
            {
              className:
                'text-[11px] sm:text-xs text-gray-500 font-medium uppercase tracking-wider',
            },
            'Minimum Order',
          ),
          React.createElement(
            'p',
            {
              className:
                'font-heading font-bold text-sm sm:text-lg text-[#202124] mt-0.5',
            },
            '$',
            restaurant.minimumOrder.toFixed(2),
          ),
        ),
      ),
      isUnavailable &&
        React.createElement(
          'div',
          {
            className:
              'px-5 py-2.5 bg-red-50 border-t border-red-100 text-xs sm:text-sm text-red-600 font-semibold flex items-center gap-2',
          },
          React.createElement('span', null, '⚠️'),
          React.createElement(
            'span',
            null,
            'This restaurant is not currently accepting orders.',
          ),
        ),
      React.createElement(
        'div',
        {
          className:
            'panda-rest-meta px-5 py-3 bg-white text-xs text-gray-600 flex items-center justify-between',
        },
        React.createElement('span', null, restaurant.tagline),
        restaurant.openNow
          ? React.createElement(
              'span',
              { className: 'text-[#65A30D] font-bold hidden sm:inline' },
              '\uD83D\uDFE2 Open for delivery now',
            )
          : React.createElement(
              'span',
              { className: 'text-gray-500 font-bold hidden sm:inline' },
              '\u26AA Closed right now',
            ),
      ),
    ),
    React.createElement(
      'div',
      {
        className:
          'panda-rest-grid grid grid-cols-1 lg:grid-cols-12 gap-8 items-start',
      },
      React.createElement(
        'div',
        { className: 'lg:col-span-8 space-y-6' },
        React.createElement(
          'div',
          {
            className:
              'bg-white rounded-2xl p-4 sm:p-6 border border-gray-200/80 shadow-xs space-y-4',
          },
          React.createElement(
            'div',
            {
              className:
                'flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-3',
            },
            React.createElement(
              'h2',
              {
                className:
                  'text-2xl font-heading font-extrabold text-[#202124]',
              },
              'Menu',
            ),
            React.createElement(
              'button',
              {
                onClick: () => setOnlyVegetarian(!onlyVegetarian),
                className: `px-3 py-1.5 rounded-full text-xs font-heading font-bold transition-all flex items-center gap-1.5 self-start sm:self-auto ${
                  onlyVegetarian
                    ? 'bg-[#65A30D] text-white ring-2 ring-emerald-300'
                    : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
                }`,
              },
              React.createElement('span', null, '\uD83C\uDF31'),
              React.createElement('span', null, 'VEGETARIAN ONLY'),
            ),
          ),
          React.createElement(
            'div',
            { className: 'space-y-1' },
            React.createElement(
              'label',
              {
                className:
                  'text-xs font-bold text-gray-500 uppercase tracking-wider block',
              },
              'Search This Menu',
            ),
            React.createElement(
              'div',
              { className: 'relative' },
              React.createElement(Search, {
                className:
                  'w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400',
              }),
              React.createElement('input', {
                type: 'text',
                placeholder: 'Search items, sides, or dietary tags...',
                value: menuSearch,
                onChange: e => setMenuSearch(e.target.value),
                className:
                  'w-full pl-10 pr-4 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-xs text-[#202124] focus:outline-none focus:ring-2 focus:ring-[#E53935] focus:bg-white',
              }),
            ),
          ),
          React.createElement(
            'div',
            {
              className:
                'flex items-center gap-2 overflow-x-auto pb-1 pt-1 hide-scrollbar',
            },
            menuCategories.map(cat =>
              React.createElement(
                'button',
                {
                  key: cat,
                  onClick: () => setSelectedMenuCategory(cat),
                  className: `flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider transition-all ${
                    selectedMenuCategory === cat
                      ? 'bg-[#202124] text-white shadow-xs'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`,
                },
                cat,
              ),
            ),
          ),
        ),
        React.createElement(
          'div',
          { className: 'space-y-4' },
          filteredItems.length === 0
            ? React.createElement(
                'div',
                {
                  className:
                    'bg-white rounded-2xl p-8 text-center text-gray-500 border border-gray-200',
                },
                React.createElement(
                  'p',
                  { className: 'font-semibold text-sm' },
                  'No items found matching your filters.',
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => {
                      setMenuSearch('');
                      setOnlyVegetarian(false);
                      setSelectedMenuCategory('All');
                    },
                    className:
                      'mt-2 text-xs text-[#E53935] font-bold hover:underline',
                  },
                  'Clear search filters',
                ),
              )
            : filteredItems.map(item => {
                const isExpanded = !!expandedDescriptions[item.id];
                const cartItem = cartItems.find(c => c.menuItemId === item.id);
                return React.createElement(
                  'div',
                  {
                    key: item.id,
                    onClick: isUnavailable
                      ? undefined
                      : () => onOpenItemModal(item),
                    className: `group bg-white rounded-2xl p-4 sm:p-5 border border-gray-200/80 hover:border-[#E53935]/50 hover:shadow-md transition-all flex flex-col sm:flex-row gap-4 justify-between ${
                      isUnavailable
                        ? 'opacity-60 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`,
                  },
                  React.createElement(
                    'div',
                    {
                      className:
                        'relative w-full sm:w-36 h-36 sm:h-28 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100',
                    },
                    React.createElement('img', {
                      src: item.image,
                      alt: item.name,
                      className:
                        'w-full h-full object-cover group-hover:scale-105 transition-transform duration-300',
                      referrerPolicy: 'no-referrer',
                      onError: e => {
                        e.target.onerror = null;
                        e.target.src = '/meal_default.png';
                      },
                    }),
                    item.isVegetarian &&
                      React.createElement(
                        'span',
                        {
                          className:
                            'absolute top-2 left-2 bg-[#65A30D] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-xs',
                        },
                        'Veg',
                      ),
                    item.isPopular &&
                      React.createElement(
                        'span',
                        {
                          className:
                            'absolute top-2 right-2 bg-[#E53935] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-xs',
                        },
                        'Popular',
                      ),
                  ),
                  React.createElement(
                    'div',
                    { className: 'flex-1 flex flex-col justify-between' },
                    React.createElement(
                      'div',
                      null,
                      React.createElement(
                        'div',
                        { className: 'flex items-start justify-between gap-2' },
                        React.createElement(
                          'h3',
                          {
                            className:
                              'font-heading font-bold text-base sm:text-lg text-[#202124] group-hover:text-[#E53935] transition-colors leading-snug',
                          },
                          item.name,
                        ),
                      ),
                      item.isVegetarian &&
                        React.createElement(
                          'span',
                          {
                            className:
                              'text-[11px] font-bold text-[#65A30D] uppercase tracking-wider block mt-0.5',
                          },
                          'Vegetarian',
                        ),
                      React.createElement(
                        'p',
                        {
                          className:
                            'text-xs sm:text-sm text-gray-600 mt-1.5 leading-relaxed',
                        },
                        isExpanded
                          ? item.fullDescription || item.description
                          : item.description,
                        !isExpanded &&
                          item.fullDescription &&
                          React.createElement(
                            'button',
                            {
                              onClick: e => toggleExpand(item.id, e),
                              className:
                                'text-[#E53935] font-semibold ml-1 hover:underline text-xs',
                            },
                            'more',
                          ),
                        isExpanded &&
                          React.createElement(
                            'button',
                            {
                              onClick: e => toggleExpand(item.id, e),
                              className:
                                'text-[#E53935] font-semibold ml-1 hover:underline text-xs',
                            },
                            'less',
                          ),
                      ),
                    ),
                    React.createElement(
                      'div',
                      {
                        className:
                          'flex items-center justify-between mt-4 pt-2 border-t border-gray-100 sm:border-0 sm:pt-0',
                      },
                      React.createElement(
                        'div',
                        {
                          className:
                            'font-heading font-bold text-base sm:text-lg text-[#202124]',
                        },
                        '$',
                        item.price.toFixed(2),
                      ),
                      cartItem
                        ? React.createElement(
                            'div',
                            {
                              onClick: e => e.stopPropagation(),
                              className:
                                'flex items-center gap-2 bg-panda-tint border border-panda-soft/50 rounded-xl px-2 py-1',
                            },
                            React.createElement(
                              'button',
                              {
                                onClick: () =>
                                  onUpdateQuantity(
                                    cartItem.id,
                                    cartItem.quantity - 1,
                                  ),
                                className:
                                  'w-7 h-7 rounded-lg bg-white border border-panda-red text-[#E53935] flex items-center justify-center hover:bg-panda-tint',
                              },
                              React.createElement(Minus, {
                                className: 'w-3.5 h-3.5',
                              }),
                            ),
                            React.createElement(
                              'span',
                              {
                                className:
                                  'font-bold text-sm text-[#202124] min-w-[18px] text-center',
                              },
                              cartItem.quantity,
                            ),
                            React.createElement(
                              'button',
                              {
                                onClick: () =>
                                  onUpdateQuantity(
                                    cartItem.id,
                                    cartItem.quantity + 1,
                                  ),
                                className:
                                  'w-7 h-7 rounded-lg bg-[#E53935] text-white flex items-center justify-center hover:bg-[#d32f2f]',
                              },
                              React.createElement(Plus, {
                                className: 'w-3.5 h-3.5',
                              }),
                            ),
                          )
                        : React.createElement(
                            'button',
                            {
                              onClick: e => {
                                e.stopPropagation();
                                if (isUnavailable) return;
                                onOpenItemModal(item);
                              },
                              disabled: isUnavailable,
                              className:
                                'px-4 py-2 rounded-xl border border-panda-soft/50 bg-panda-tint/80 hover:bg-[#E53935] hover:text-white hover:border-[#E53935] text-[#E53935] font-heading font-bold text-xs transition-all flex items-center gap-1.5 shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-panda-tint/80 disabled:hover:text-[#E53935]',
                            },
                            React.createElement(Plus, {
                              className: 'w-3.5 h-3.5',
                            }),
                            React.createElement(
                              'span',
                              null,
                              isUnavailable ? 'Unavailable' : 'Add Item',
                            ),
                          ),
                    ),
                  ),
                );
              }),
        ),
      ),
      React.createElement(
        'div',
        { className: 'hidden lg:block lg:col-span-4 sticky top-28' },
        React.createElement(CartSidebar, {
          items: cartItems,
          onUpdateQuantity: onUpdateQuantity,
          onRemoveItem: onRemoveItem,
          onEditItem: onEditItem,
          onCheckout: onCheckout,
          onSaveForLater: onSaveForLater,
          onCreateGroupOrder: onCreateGroupOrder,
          isSavingForLater: isSavingForLater,
          isCreatingGroupOrder: isCreatingGroupOrder,
          appliedPromo: appliedPromo,
          onApplyPromo: onApplyPromo,
          discountAmount: discountAmount,
          deliveryFee: restaurant.deliveryFee,
          taxRate: taxRate,
          disabled: isUnavailable,
        }),
      ),
    ),
  );
};
